import Axios from "axios";
import { useCallback, useEffect, useRef } from "react";

import { NetworkError } from "@btnet/services-btnet/errors";

import { isCanceledAPIRequestError } from "types/apiResponse/apiResponse";

/**
 * Checks if an error is from a cancelled grid request (supports both fetch AbortController and axios cancellation).
 */
export function isCancelledGridRequestError(error: unknown): boolean {
    if (Axios.isCancel(error)) {
        return true;
    }
    if (isCanceledAPIRequestError(error)) {
        return true;
    }
    if (error instanceof DOMException && error.name === "AbortError") {
        return true;
    }
    // APIHandler's fetch path re-wraps an aborted request's AbortError as a NetworkError,
    // so unwrap it to detect cancellation on the non-cancellable-version code path.
    if (error instanceof NetworkError && error.domException?.name === "AbortError") {
        return true;
    }
    return false;
}

/**
 * Hook for function components: provides a getSignal() function that
 * cancels the previous in-flight grid request whenever a new one is made.
 * Also cancels on unmount.
 */
export function useCancellableGridRequest() {
    const abortControllerRef = useRef<AbortController | null>(null);

    const getSignal = useCallback((): AbortSignal => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        return abortControllerRef.current.signal;
    }, []);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    return { getSignal };
}

/**
 * Class-based equivalent of useCancellableGridRequest for class components.
 * Call getSignal() before each grid request, and cancel() in componentWillUnmount.
 */
export class GridRequestCanceller {
    private abortController: AbortController | null = null;

    getSignal(): AbortSignal {
        this.abortController?.abort();
        this.abortController = new AbortController();
        return this.abortController.signal;
    }

    cancel(): void {
        this.abortController?.abort();
    }
}
